import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import VpnLockIcon from "@mui/icons-material/VpnLock";
import PageShell from "./PageShell";
import PageContainer from "./components/PageContainer";
import PageHeader from "./components/PageHeader";
import DataCard, { tableHeaderCellSx } from "./components/DataCard";
import apiClient from "./utils/apiClient";

const formatTimestamp = (ms) => {
  if (!ms) return "—";
  const date = new Date(ms);
  return Number.isNaN(date.getTime()) ? String(ms) : date.toLocaleString();
};

export default function AuthSessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get("/admin/auth-session-list");
      setSessions(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error("Error fetching auth sessions:", err);
      setError(err?.message || "Failed to load auth sessions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const sorted = useMemo(
    () => [...sessions].sort((a, b) => (Number(b?.created) || 0) - (Number(a?.created) || 0)),
    [sessions]
  );

  return (
    <PageShell>
      <PageContainer>
        <PageHeader
          icon={<VpnLockIcon />}
          title="Auth Sessions"
          subtitle={`${sorted.length} live session${sorted.length === 1 ? "" : "s"} — dead sessions are swept every time this page loads`}
          action={
            <Button
              variant="outlined"
              onClick={fetchSessions}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} /> : <RefreshIcon />}
              sx={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.4)",
                "&:hover": {
                  borderColor: "#fff",
                  backgroundColor: "rgba(255,255,255,0.06)",
                },
              }}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </Button>
          }
        />

        {error && <Alert severity="error">{error}</Alert>}

        <Typography variant="caption" color="rgba(255,255,255,0.65)">
          A session is swept if it's marked expired/revoked, past its expiry, older than the
          12-hour session cap, or belongs to a user that no longer exists.
        </Typography>

        <DataCard noPadding>
          {loading && sessions.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeaderCellSx}>User</TableCell>
                    <TableCell sx={tableHeaderCellSx}>Status</TableCell>
                    <TableCell sx={tableHeaderCellSx}>Created</TableCell>
                    <TableCell sx={tableHeaderCellSx}>Expires</TableCell>
                    <TableCell sx={tableHeaderCellSx}>Last Used</TableCell>
                    <TableCell sx={tableHeaderCellSx}>IP Address</TableCell>
                    <TableCell sx={tableHeaderCellSx}>User Agent</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sorted.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                        No live sessions.
                      </TableCell>
                    </TableRow>
                  ) : (
                    sorted.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.user_id || "N/A"}</TableCell>
                        <TableCell>{row.status || "N/A"}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{formatTimestamp(row.created)}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{formatTimestamp(row.expires_at)}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{formatTimestamp(row.last_used_at)}</TableCell>
                        <TableCell>{row.ip_address || "—"}</TableCell>
                        <TableCell sx={{ maxWidth: 320, wordBreak: "break-word" }}>{row.user_agent || "—"}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataCard>
      </PageContainer>
    </PageShell>
  );
}
